import { Alert, Button, Spin } from 'antd';
import { ensureOrderInvoiceNos, getOrderDetailBatch } from 'apis/oms';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SGASUInvoice from './sg-asu-invoice';
import SGGXInvoice from './sg-gx-invoice';
import SGASUGSTInvoice from './sg-asu-gst-invoice';
import HKInvoice from './hk-invoice';
import FranceInvoice, { resolveFranceInvoiceVariant } from './fr-invoice';
import DubaiInvoice from './dubai-invoice';
import {
  loadHkTransferCostBySourceProductId,
  OrderDetailBatchReturnType,
  TransferProductCost,
} from './common';
import { STOCK_PLACE_MAP } from 'constant/stock-place';

const OrderInvoiceV2 = () => {
  const [searchParams] = useSearchParams();
  const [orderList, setOrderList] = useState<OrderDetailBatchReturnType[]>([]);
  const [transferCostBySourceProductId, setTransferCostBySourceProductId] =
    useState<Record<number, TransferProductCost>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const orderIdList = useMemo(
    () =>
      searchParams
        .get('orderIdList')
        ?.split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id)) || [],
    [searchParams],
  );

  useEffect(() => {
    let canceled = false;

    if (!orderIdList.length) {
      setOrderList([]);
      setErrorMessage('缺少订单 ID');
      return;
    }

    setLoading(true);
    setErrorMessage(undefined);
    setTransferCostBySourceProductId({});

    ensureOrderInvoiceNos({ ids: orderIdList })
      .then(() => getOrderDetailBatch({ ids: orderIdList }))
      .then(async (res) => {
        const transferCosts = await loadHkTransferCostBySourceProductId(res);
        if (canceled) return;
        setOrderList(res);
        setTransferCostBySourceProductId(transferCosts);
      })
      .catch((error: unknown) => {
        if (canceled) return;
        setOrderList([]);
        setErrorMessage((error as Error)?.message || '生成 Invoice No 失败');
      })
      .finally(() => {
        if (canceled) return;
        setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [orderIdList]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="p-6">
        <Alert type="error" message={errorMessage} />
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pb-24 print:pb-0">
      <div className="flex flex-col items-center justify-center gap-4 print:gap-0">
        {orderList.map((order, index) => {
          const stockPlace = order.orderItems[0]?.product?.stockPlace;
          const pageBreakClass =
            index === orderList.length - 1
              ? 'print:break-after-auto'
              : 'print:break-after-page';

          if (stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU) {
            return (
              <div className={pageBreakClass} key={order.id}>
                <SGASUInvoice order={order} />
              </div>
            );
          }

          if (stockPlace === STOCK_PLACE_MAP.SINGAPORE_GX) {
            return (
              <div className={pageBreakClass} key={order.id}>
                <SGGXInvoice order={order} />
              </div>
            );
          }

          if (stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU_GST) {
            return (
              <div className={pageBreakClass} key={order.id}>
                <SGASUGSTInvoice order={order} />
              </div>
            );
          }

          if (stockPlace === STOCK_PLACE_MAP.HONGKONG) {
            return (
              <div className={pageBreakClass} key={order.id}>
                <HKInvoice
                  order={order}
                  transferCostBySourceProductId={transferCostBySourceProductId}
                />
              </div>
            );
          }

          if (stockPlace === STOCK_PLACE_MAP.FRANCE) {
            return (
              <div className={pageBreakClass} key={order.id}>
                <FranceInvoice
                  order={order}
                  variant={resolveFranceInvoiceVariant(order.taxRegime)}
                />
              </div>
            );
          }

          if (stockPlace === STOCK_PLACE_MAP.DUBAI) {
            return (
              <div className={pageBreakClass} key={order.id}>
                <DubaiInvoice order={order} />
              </div>
            );
          }

          return null;
        })}
      </div>

      <div className="fixed bottom-6 left-1/2 z-10 -translate-x-1/2 print:hidden">
        <Button type="primary" onClick={handlePrint}>
          打印发票
        </Button>
      </div>
    </div>
  );
};

export default OrderInvoiceV2;
